/**
 * Web-components-core loader.
 *
 * Loads element constructors via the lib's tree-shakeable `./define`
 * entry, split into two dynamic-imported chunks:
 *
 * - `wcCriticalDefines` — the 41 elements present in first-paint SSR HTML
 *   plus the lazy header-triggered components (Cmd+K, drawer). Loaded
 *   eagerly so the upgrade fires before the user sees raw unstyled tags.
 * - `wcRestDefines` — the remaining ~40 (Editor, Table, Combobox, …).
 *   Loaded at `requestIdleCallback`, past TTI, off the TBT window.
 *
 * Each split file uses explicit named imports of the lib's `defineX`
 * functions; the lib's `sideEffects` whitelist allows Vite to tree-shake
 * unreferenced element modules out of each chunk. Net effect: the
 * critical chunk contains only the 41 element graphs instead of the
 * full 80+ that the previous `./elements` umbrella import dragged in
 * (137 KB → expected ~70-80 KB).
 *
 * `parallel: true` lets Vue hydration overlap with chunk fetch+parse.
 */
// The global `/styles` entry was removed in the framework-free packages. This
// host owns its own Tailwind build over the library, so no stylesheet import is
// needed; element-internal styles ship as shadow-DOM adopted sheets.
import { withoutMonacoPrewarm, ensureHeavyDefines } from '~/utils/wcHeavyDefines';
import { ensureDiagramDefine } from '~/utils/wcDiagramDefine';

/**
 * Monaco's deepClone (gl) stack-overflows when options contain document.body
 * as ariaContainerElement — Vue's reactive graph on body creates cycles.
 * Fix: swap document.body for a clean container before editor.create().
 */
function patchMonacoCreate(monacoObj: any) {
  const origCreate: Function = monacoObj.editor.create.bind(monacoObj.editor);
  monacoObj.editor.create = function (
    domElement: HTMLElement,
    options?: any,
    override?: object,
  ) {
    if (options?.ariaContainerElement instanceof HTMLElement) {
      const clean = document.createElement('div');
      clean.style.position = 'absolute';
      clean.style.overflow = 'hidden';
      clean.style.width = '0';
      clean.style.height = '0';
      options.ariaContainerElement.appendChild(clean);
      options = { ...options, ariaContainerElement: clean };
    }
    return origCreate(domElement, options, override);
  };
}

let _monacoValue: any = (globalThis as any).monaco;
Object.defineProperty(globalThis, 'monaco', {
  configurable: true,
  enumerable: true,
  get() { return _monacoValue; },
  set(value) {
    _monacoValue = value;
    if (value?.editor?.create) patchMonacoCreate(value);
    Object.defineProperty(globalThis, 'monaco', {
      configurable: true,
      enumerable: true,
      writable: true,
      value,
    });
  },
});

// Heavy elements (Monaco editor / QR code / document validator) load lazily
// once a page renders one of their tags — on initial load OR after an SPA nav.
// The shared latch + Monaco-prewarm guard live in `wcHeavyDefines`; the
// `<e-document-validator>` block also calls `ensureHeavyDefines()` from its own
// `onMounted` to defeat the `<ClientOnly>` mount race the DOM-poll here can lose.

// Every tag registered by `defineAllRest()` (family roots + subparts). The
// idle upgrade only pays for the ~200 KB rest chunk when the current page
// actually renders one of these; a page like the homepage renders none and
// never downloads it. Custom-element upgrades are retroactive (`define()`
// upgrades matching elements already in the DOM), so a late define is
// correctness-safe — only the unstyled window matters, and these are all
// below-fold / interaction-revealed elements.
const REST_TAGS =
  'e-video,e-menu,e-input-number,e-input-otp,e-input-upload,' +
  'e-tab,e-chat,e-splitter,e-select,e-checkbox,e-radio,e-switch,' +
  'e-combobox,e-date-picker,e-slider,e-placeholder';

let restLoaded = false;
async function ensureRestDefines(): Promise<void> {
  if (restLoaded) return;
  restLoaded = true;
  await withoutMonacoPrewarm(async () => {
    const { defineAllRest } = await import('~/utils/wcRestDefines');
    defineAllRest();
  });
}

// Rest elements upgrade on two triggers:
//  1. idle + present in DOM — covers SSR'd tags (docs pages: e-tab, e-select…)
//     without charging tag-less pages anything;
//  2. first user interaction — covers elements CREATED on demand after a
//     click/keystroke (composed drawers). The
//     interaction marks the end of the measured TBT window, and retroactive
//     upgrade fixes the freshly-created element even if the chunk lands a
//     beat later.
function scheduleRest(): void {
  const kick = () => {
    void (async () => {
      if (document.querySelector(REST_TAGS)) await ensureRestDefines();
      await ensureHeavyDefines();
      await ensureDiagramDefine();
    })();
  };
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(kick, { timeout: 4000 });
  } else {
    setTimeout(kick, 1000);
  }
  // First interaction defines the full rest set. An earlier optimization
  // narrowed this to the Cmd+K palette only, on the assumption that command was
  // the sole interaction-created rest element on a tag-less page — but that
  // left <e-chat> (mounted on chat open, not SSR'd) undefined and dead. Any
  // other interaction-created rest element would break the same way, so this
  // loads the full set on first interaction (post-TTI, off the TBT window).
  const opts: AddEventListenerOptions = { once: true, passive: true, capture: true };
  window.addEventListener('pointerdown', () => void ensureRestDefines(), opts);
  window.addEventListener('keydown', () => void ensureRestDefines(), opts);
}

export default defineNuxtPlugin({
  parallel: true,
  async setup(nuxtApp) {
    const { defineAllCritical } = await withoutMonacoPrewarm(() =>
      import('~/utils/wcCriticalDefines'),
    );
    defineAllCritical();
    // Install the `flag:` icon provider for <e-icon> (iconify flag collection).
    // Cheap to register — the collection JSON loads lazily on the first flag.
    const { registerFlagIcons } = await import('~/utils/registerFlagIcons');
    registerFlagIcons();
    useEconnectReady().value = true;
    scheduleRest();
    // After every SPA navigation, re-run the presence checks: a nav from a
    // tag-less page (e.g. /nl) to a docs page can introduce rest-set tags
    // (e-tab, e-select, e-date-picker…) or heavy editor/validator elements.
    // Both ensure-functions are idempotent latches.
    nuxtApp.hook('page:finish', () => {
      void nextTick(async () => {
        if (document.querySelector(REST_TAGS)) await ensureRestDefines();
        await ensureHeavyDefines();
        await ensureDiagramDefine();
      });
    });
  },
});
